import React, { FC, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useIsSheet } from "./useIsSheet";

export interface PickerLayerProps {
  open: boolean;
  onClose: () => void;
  /** The sheet's heading on phones, and the dialog's name everywhere. */
  title: string;
  /** What counts as "inside" on desktop: the field and its popover. A press outside it closes the popover. */
  wrapperRef: RefObject<HTMLElement>;
  /** Where the desktop popover sits and how wide it is (it is `absolute` under the wrapper by default). */
  desktopClassName?: string;
  /** Pinned under the content: the main action. */
  footer?: ReactNode;
  children: ReactNode;
  /** Test hook: which picker this is. */
  name?: string;
  /**
   * How it opens from `md` up. `popover`: under its field. `modal`: centred over the page with a backdrop, for
   * the wide calendars: it has room whatever the page is under it (a popover under a field near the foot of a
   * short page ran over the footer).
   */
  desktopMode?: "popover" | "modal";
}

/**
 * 1NOW-11176: the Design B picker surface, for the location list, the calendar and the times: a bottom
 * sheet on phones (a handle, a sentence-case title, a round close button) and a popover under its field
 * from `md` up. White surface, 1px hairline, a soft shadow because it floats, 12px radius. The pattern
 * is the dropdown panels of sixt.com and booking.com.
 *
 * It only draws; each picker owns what it picks and when it closes.
 */
const PickerLayer: FC<PickerLayerProps> = ({
  open,
  onClose,
  title,
  wrapperRef,
  desktopClassName = "absolute left-0 top-full mt-2 w-full min-w-[300px]",
  footer,
  children,
  name,
  desktopMode = "popover",
}) => {
  const sheet = useIsSheet();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Escape closes either surface.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // A popover must not run over the footer: opened under its field, if it would reach the footer it flips up when
  // there is room above the field, and otherwise opens as a centred modal. (Popovers pinned under the header bar
  // are `fixed` and have the whole screen.)
  const [flip, setFlip] = useState<"below" | "above" | "modal">("below");
  const pinned = desktopClassName.includes("fixed");
  const modal = !sheet && (desktopMode === "modal" || flip === "modal");

  useLayoutEffect(() => {
    if (!open) setFlip("below");
  }, [open]);
  useLayoutEffect(() => {
    if (!open || sheet || desktopMode === "modal" || pinned || flip !== "below") return;
    const panel = popoverRef.current;
    const field = wrapperRef.current;
    if (!panel || !field) return;
    const footer = document.querySelector("footer");
    const limit = footer ? footer.getBoundingClientRect().top : Infinity;
    const box = panel.getBoundingClientRect();
    if (box.bottom <= limit) return;
    setFlip(box.height + 8 <= field.getBoundingClientRect().top ? "above" : "modal");
  }, [open, sheet, desktopMode, pinned, flip, wrapperRef]);

  // A press outside the field and its popover closes the popover. The sheet and the modal have a backdrop for that.
  useEffect(() => {
    if (!open || sheet || modal) return;
    const onDown = (e: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, sheet, modal, onClose, wrapperRef]);

  // The page behind a sheet or a modal does not scroll.
  useEffect(() => {
    if (!open || !(sheet || modal)) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, sheet, modal]);

  // A popover that opens below the fold is brought into view (a sheet is already on screen).
  useEffect(() => {
    if (open && !sheet && !modal && flip === "below") popoverRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [open, sheet, modal, flip]);

  if (!open) return null;

  if (modal) {
    return createPortal(
      <div className="cpb fixed inset-0 z-[80] flex items-center justify-center p-4" data-picker={name}>
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="relative flex max-h-[min(90vh,820px)] w-full max-w-[780px] flex-col rounded-xl bg-card-color text-text-color shadow-[0_8px_30px_rgba(0,0,0,0.2)]"
        >
          <div className="flex shrink-0 items-center justify-between px-6 pb-1 pt-5">
            <h2 className="text-lg font-semibold leading-tight">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-text-color/15 text-text-color hover:bg-text-color/5"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-2 pt-2">{children}</div>
          {footer && <div className="shrink-0 border-t border-text-color/10 px-6 pb-5 pt-4">{footer}</div>}
        </div>
      </div>,
      document.body,
    );
  }

  if (sheet) {
    return createPortal(
      <div className="cpb fixed inset-0 z-[80]" data-picker={name}>
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-2xl bg-card-color text-text-color shadow-[0_-8px_30px_rgba(0,0,0,0.18)]"
        >
          <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-text-color/20" aria-hidden="true" />
          <div className="flex shrink-0 items-center justify-between px-5 pb-1 pt-3">
            <h2 className="text-lg font-semibold leading-tight">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-text-color/15 text-text-color hover:bg-text-color/5"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-3 pt-2">{children}</div>
          {footer && <div className="shrink-0 border-t border-text-color/10 px-5 pb-5 pt-3">{footer}</div>}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={title}
      data-picker={name}
      className={`cpb z-50 rounded-xl border border-text-color/10 bg-card-color p-4 text-text-color shadow-[0_8px_30px_rgba(0,0,0,0.14)] ${
        flip === "above" ? desktopClassName.replace("top-full mt-2", "bottom-full mb-2") : desktopClassName
      }`}
    >
      {children}
      {footer && <div className="mt-4 border-t border-text-color/10 pt-4">{footer}</div>}
    </div>
  );
};

export default PickerLayer;
